import { useEffect, useState, type ReactNode } from 'react';
import { ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import type { ReportPreview } from '../lib/api';
import { openPreviewWindow } from '../lib/preview-window';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';

interface Props {
  preview: ReportPreview;
  /** Tailwind height class for the docked frame. */
  heightClass?: string;
  actions?: ReactNode;
}

/**
 * The rendered email inside a drawer, plus the two escapes from it: a
 * viewport-filling overlay and a standalone tab. Email HTML is laid out for a
 * 600px+ body, which a 4xl drawer cannot show without the reader guessing.
 */
export function EmailPreviewFrame({ preview, heightClass = 'h-[600px]', actions }: Props) {
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
        <CardTitle className="text-base">Rendered email</CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          {actions}
          <Button variant="outline" size="sm" onClick={() => setMaximized(true)}>
            <Maximize2 className="h-3.5 w-3.5" />
            Full screen
          </Button>
          <Button variant="outline" size="sm" onClick={() => openPreviewWindow(preview)}>
            <ExternalLink className="h-3.5 w-3.5" />
            New tab
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        <div
          className={
            maximized
              ? 'fixed inset-0 z-[100] flex flex-col bg-white'
              : `border-t border-border bg-white ${heightClass}`
          }
        >
          {maximized ? (
            <div className="flex items-center justify-between gap-3 border-b border-line bg-panel px-4 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {preview.report_name || preview.report_id}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {preview.target_date} · {preview.subject}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="hidden text-xs text-muted-foreground sm:inline">Esc to exit</span>
                <Button variant="outline" size="sm" onClick={() => openPreviewWindow(preview)}>
                  <ExternalLink className="h-3.5 w-3.5" />
                  New tab
                </Button>
                <Button variant="outline" size="sm" onClick={() => setMaximized(false)}>
                  <Minimize2 className="h-3.5 w-3.5" />
                  Exit full screen
                </Button>
              </div>
            </div>
          ) : null}
          <iframe
            title="Email preview"
            srcDoc={preview.html}
            sandbox=""
            className={maximized ? 'min-h-0 w-full flex-1' : 'block h-full w-full'}
          />
        </div>
      </CardContent>
    </Card>
  );
}
